import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, ShoppingCart, Package, Boxes, AlertTriangle, DollarSign, BarChart3, Users, ScrollText, LogOut, MoreHorizontal, Settings, PackagePlus } from 'lucide-react';
import { useAuth } from '../lib/auth';

// 5 principais na barra inferior + "Mais" que abre grid com todas as demais páginas
const NAV = [
  { to: '/dashboard', label: 'Painel', icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/vender', label: 'Vender', icon: ShoppingCart, testid: 'nav-vender' },
  { to: '/products', label: 'Produtos', icon: Package, testid: 'nav-products' },
  { to: '/stock', label: 'Estoque', icon: Boxes, testid: 'nav-stock' },
];

export const MORE_ITEMS = [
  { to: '/entradas', label: 'Entradas', icon: PackagePlus, testid: 'nav-entradas' },
  { to: '/avaria', label: 'Avarias / Perdas', icon: AlertTriangle, testid: 'nav-avaria' },
  { to: '/finance', label: 'Financeiro', icon: DollarSign, testid: 'nav-finance' },
  { to: '/reports', label: 'Relatórios', icon: BarChart3, testid: 'nav-reports' },
  { to: '/employees', label: 'Funcionários', icon: Users, testid: 'nav-employees', adminOnly: true },
  { to: '/audit', label: 'Histórico', icon: ScrollText, testid: 'nav-audit' },
  { to: '/settings', label: 'Configurações', icon: Settings, testid: 'nav-settings' },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between" data-testid="app-header">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-orange-500 text-white grid place-items-center font-black">LU</div>
          <div>
            <div className="font-bold text-sm leading-tight tracking-tight">LU SCHOOL SNACKS</div>
            <div className="text-[11px] text-slate-500 leading-tight">{user?.name} · {user?.role === 'admin' ? 'Admin' : 'Funcionário'}</div>
          </div>
        </div>
        <button onClick={logout} data-testid="logout-btn" className="text-slate-500 hover:text-red-500 active:scale-95 transition-transform p-2 no-tap-highlight">
          <LogOut size={20} />
        </button>
      </header>

      <main className="flex-1 pb-24 overflow-hidden">{children}</main>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(0,0,0,0.04)]" data-testid="bottom-nav">
        <div className="grid grid-cols-5 max-w-xl mx-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 no-tap-highlight active:scale-95 transition-transform ${isActive ? 'text-orange-600' : 'text-slate-500'}`}>
              <n.icon size={22} />
              <span className="text-[10px] font-semibold">{n.label}</span>
            </NavLink>
          ))}
          <NavLink to="/more" data-testid="nav-more"
            className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 no-tap-highlight active:scale-95 transition-transform ${isActive || MORE_ITEMS.some(m => loc.pathname.startsWith(m.to)) ? 'text-orange-600' : 'text-slate-500'}`}>
            <MoreHorizontal size={22} />
            <span className="text-[10px] font-semibold">Mais</span>
          </NavLink>
        </div>
      </nav>
    </div>
  );
}

export function MorePage() {
  const { user } = useAuth();
  const items = MORE_ITEMS.filter((i) => !i.adminOnly || user?.role === 'admin');
  return (
    <div className="p-4 h-full overflow-y-auto">
      <h2 className="text-xl font-black mb-3">Mais opções</h2>
      <div className="grid grid-cols-2 gap-3">
        {items.map((i) => (
          <NavLink key={i.to} to={i.to} data-testid={i.testid}
            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm active:scale-[0.98] transition-transform no-tap-highlight flex flex-col gap-2">
            <i.icon size={26} className="text-orange-600" />
            <div className="font-bold text-sm">{i.label}</div>
          </NavLink>
        ))}
      </div>
    </div>
  );
}
